import React, { useState, useEffect, useRef } from 'react'
import MoodTracker from '../Today/MoodTracker.jsx'
import ActivityTracker from '../Activities/ActivityTracker.jsx'
import Card from '../../components/Card.jsx'
import { todayISO, todayShort, calcStreak } from '../../utils/dates.js'
import * as api from '../../services/api.js'
import SectionWelcome from '../../components/SectionWelcome.jsx'

// ── Constants ────────────────────────────────────────────────────────────────

const MOOD_STATES = [
  { name: 'Sad',     color: 'var(--stone)', opacity: 0.45 },
  { name: 'Anxious', color: 'var(--stone)', opacity: 0.8  },
  { name: 'Calm',    color: 'var(--sage)',  opacity: 1    },
  { name: 'Happy',   color: 'var(--bark)',  opacity: 1    },
  { name: 'Excited', color: 'var(--accent)',opacity: 1    },
]

const ACTIVITY_TYPES = [
  { id: 'yoga',     label: 'Yoga',       emoji: '🧘' },
  { id: 'run',      label: 'Running',    emoji: '🏃' },
  { id: 'dance',    label: 'Dancing',    emoji: '💃' },
  { id: 'swim',     label: 'Swimming',   emoji: '🏊' },
  { id: 'cycle',    label: 'Cycling',    emoji: '🚴' },
  { id: 'hike',     label: 'Hiking',     emoji: '🥾' },
  { id: 'pilates',  label: 'Pilates',    emoji: '🤸' },
  { id: 'gym',      label: 'Gym',        emoji: '💪' },
  { id: 'stretch',  label: 'Stretching', emoji: '🙆' },
  { id: 'meditate', label: 'Meditation', emoji: '🪷' },
  { id: 'tennis',   label: 'Tennis',     emoji: '🎾' },
  { id: 'sport',    label: 'Team Sport', emoji: '⚽' },
]

const REFLECTION_QUESTIONS = [
  'What was one small moment this week that felt okay?',
  'Did anything make you smile, even briefly?',
  'What did your body need most this week?',
  'Is there something you did just for yourself lately?',
  "What's one thing you noticed around you today?",
  'What would feel like a gentle treat this week?',
  'When did time pass without you noticing it?',
]

const REFLECTION_EMOJIS = [
  '🌿', '🌸', '🌻', '🌙', '⭐', '💫', '🌊', '🌈',
  '🍃', '🦋', '🌺', '🌹', '🌷', '🍀', '🌾', '🕊️',
  '🌟', '✨', '💖', '💗', '💙', '💚', '💛', '🧡',
  '💜', '🤍', '☀️', '🌤️', '⛅', '🌧️', '❤️', '😌',
]

const SECTIONS = [
  { id: 'mood',        label: 'Mood' },
  { id: 'activity',    label: 'Activity' },
  { id: 'trends',      label: 'Trends' },
  { id: 'reflections', label: 'Reflections' },
]

// ── Helpers ──────────────────────────────────────────────────────────────────

function migrateEntries(entries) {
  if (!entries) return []
  return entries.map(e =>
    typeof e === 'string'
      ? { id: e, mins: null, cals: null, link: '', doAgain: null }
      : { cals: null, link: '', doAgain: null, ...e }
  )
}

function filterByDays(entries, days) {
  const cutoff = Date.now() - days * 86400000
  return entries.filter(e => e.ts >= cutoff)
}

function pickReflectionQ() {
  return REFLECTION_QUESTIONS[Math.floor(Date.now() / 604800000) % REFLECTION_QUESTIONS.length]
}

// ── Section nav ───────────────────────────────────────────────────────────────

function WellbeingSectionNav({ activeSection }) {
  function scrollTo(id) {
    const el = document.getElementById(id)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  return (
    <div className="dashboard-section-nav">
      {SECTIONS.map(s => (
        <button
          key={s.id}
          className={`dashboard-nav-btn${activeSection === s.id ? ' active' : ''}`}
          onClick={() => scrollTo(s.id)}
        >
          {s.label}
        </button>
      ))}
    </div>
  )
}

// ── Mood ──────────────────────────────────────────────────────────────────────

function MoodSection({ moods }) {
  const [view, setView]               = useState(7)
  const [showAll, setShowAll]         = useState(false)
  const [expanded, setExpanded]       = useState({})

  const slice        = filterByDays(moods, view).slice().reverse()
  const displayMoods = showAll ? moods : moods.slice(0, 3)

  function toggleExpand(i) {
    setExpanded(prev => ({ ...prev, [i]: !prev[i] }))
  }

  return (
    <>
      <MoodTracker />

      <Card label="Mood" title="Your mood history">
        <div className="wb-chart-header">
          <div className="view-toggle">
            {[7, 14, 30].map(d => (
              <button key={d} className={`view-btn${view === d ? ' active' : ''}`} onClick={() => setView(d)}>
                {d === 7 ? '1 week' : d === 14 ? '2 weeks' : 'Month'}
              </button>
            ))}
          </div>
          <span className="wb-chart-title">Mood chart</span>
        </div>

        {slice.length === 0 ? (
          <p style={{ fontSize: '0.82rem', color: 'var(--stone)', fontStyle: 'italic', padding: '1rem 0', textAlign: 'center' }}>
            No entries in this period yet.
          </p>
        ) : (
          <div className="dot-chart">
            <div className="dot-row">
              {slice.map((e, i) => {
                const s = MOOD_STATES[e.level]
                const h = Math.round(((e.level + 1) / 5) * 96)
                return (
                  <div key={i} className="dot-col" title={`${e.date} — ${s.name}`}>
                    <div className="dot-bar" style={{ height: h, background: s.color, opacity: s.opacity }} />
                    <span className="dot-date">{e.date}</span>
                  </div>
                )
              })}
            </div>
            <div className="chart-legend">
              {MOOD_STATES.map(s => (
                <span key={s.name} className="legend-item">
                  <span className="legend-dot" style={{ background: s.color }} />
                  {s.name}
                </span>
              ))}
            </div>
          </div>
        )}

        {moods.length > 0 && (
          <div style={{ marginTop: '1rem' }}>
            <p style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--stone)', marginBottom: '0.5rem' }}>
              Recent check-ins
            </p>
            {displayMoods.map((e, i) => {
              const s = MOOD_STATES[e.level]
              return (
                <div key={i} className="log-entry" style={{ alignItems: e.note ? 'flex-start' : 'center' }}>
                  <span className="log-icon" style={{ color: s.color }}>●</span>
                  <span className="log-date">{e.date}</span>
                  <span className="log-mood">{s.name}</span>
                  {e.note && (
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p className={`mood-history-note${expanded[i] ? ' expanded' : ''}`}>{e.note}</p>
                      {e.note.length > 80 && (
                        <button className="expand-toggle" onClick={() => toggleExpand(i)}>
                          {expanded[i] ? 'less' : 'more'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
            {moods.length > 3 && (
              <button className="wb-show-more" onClick={() => setShowAll(s => !s)}>
                {showAll ? 'Show less' : `Show ${moods.length - 3} more`}
              </button>
            )}
          </div>
        )}
      </Card>
    </>
  )
}

// ── Activity ──────────────────────────────────────────────────────────────────

function ActivitySection({ activityLog, hiddenActivities, onToggleActivity }) {
  const [monthlyTarget, setMonthlyTarget] = useState(0)
  const [settingTarget, setSettingTarget] = useState(false)
  const [targetInput,   setTargetInput]   = useState('')
  const [manageOpen,    setManageOpen]    = useState(false)

  const today      = todayISO()
  const weekAgo    = new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10)
  const monthStart = today.slice(0, 7)

  const visibleActivities = ACTIVITY_TYPES.filter(a => !hiddenActivities.includes(a.id))

  // Monthly minutes
  let totalMinsMonth = 0
  const dailyMins = {}
  Object.keys(activityLog).forEach(d => {
    if (d.startsWith(monthStart)) {
      const dayMins = migrateEntries(activityLog[d]).reduce((s, e) => s + (e.mins || 0), 0)
      dailyMins[d]    = dayMins
      totalMinsMonth += dayMins
    }
  })

  // Days 1→today for bar chart
  const now          = new Date()
  const currentDay   = now.getDate()
  const monthDays    = Array.from({ length: currentDay }, (_, i) => {
    const iso = `${monthStart}-${String(i + 1).padStart(2, '0')}`
    return { iso, mins: dailyMins[iso] || 0 }
  })
  const maxDayMins = Math.max(...monthDays.map(d => d.mins), 1)
  const hasDayData = monthDays.some(d => d.mins > 0)

  // This week top activities (visible only)
  const weekEntries = []
  Object.keys(activityLog).forEach(d => {
    if (d >= weekAgo && d <= today) migrateEntries(activityLog[d]).forEach(e => weekEntries.push(e))
  })
  const actCounts = {}
  visibleActivities.forEach(a => { actCounts[a.id] = 0 })
  weekEntries.forEach(e => { if (actCounts[e.id] !== undefined) actCounts[e.id]++ })
  const topActs = visibleActivities
    .filter(a => actCounts[a.id] > 0)
    .sort((a, b) => actCounts[b.id] - actCounts[a.id])
    .slice(0, 6)

  useEffect(() => {
    api.getSetting('act_monthly_mins', '0').then(v => setMonthlyTarget(parseInt(v) || 0))
  }, [])

  function saveTarget() {
    const t = parseInt(targetInput) || 0
    setMonthlyTarget(t)
    api.saveSetting('act_monthly_mins', String(t))
    setSettingTarget(false)
    setTargetInput('')
  }

  const monthName = now.toLocaleString('default', { month: 'long' })
  const targetPct = monthlyTarget > 0
    ? Math.min(100, Math.round((totalMinsMonth / monthlyTarget) * 100))
    : 0

  return (
    <>
      <ActivityTracker />

      <Card label="Activity" title="Your progress">
        {/* Monthly target */}
        {monthlyTarget > 0 && (
          <div style={{ marginBottom: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.78rem', marginBottom: '4px' }}>
              <span style={{ color: 'var(--stone)' }}>Monthly goal — {monthName}</span>
              <span style={{ fontWeight: 600, color: 'var(--bark)' }}>{totalMinsMonth} / {monthlyTarget} min</span>
            </div>
            <div className="wb-target-bar-wrap">
              <div className="wb-target-bar-fill" style={{ width: `${targetPct}%` }} />
            </div>
            <p style={{ fontSize: '0.7rem', color: 'var(--stone)', textAlign: 'right', marginTop: '2px' }}>
              {targetPct}% complete
            </p>
          </div>
        )}

        {/* Daily minutes bar chart */}
        {hasDayData && (
          <div style={{ marginBottom: '1.25rem' }}>
            <p style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--stone)', marginBottom: '2px' }}>
              Daily minutes — {monthName}
            </p>
            <div className="wb-daily-bar-chart">
              {monthDays.map(({ iso, mins }) => (
                <div
                  key={iso}
                  className={`wb-daily-bar${iso === today ? ' today-bar' : ''}`}
                  style={{ height: `${Math.max(3, Math.round((mins / maxDayMins) * 100))}%` }}
                  title={`${iso.slice(8)}: ${mins} min`}
                />
              ))}
            </div>
            <p style={{ fontSize: '0.7rem', color: 'var(--stone)', textAlign: 'right' }}>
              {totalMinsMonth} min total this month
            </p>
          </div>
        )}

        {/* This week summary */}
        {topActs.length > 0 && (
          <div style={{ marginBottom: '1.25rem' }}>
            <p style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--stone)', marginBottom: '0.5rem' }}>
              This week
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {topActs.map(a => (
                <span key={a.id} style={{
                  display: 'inline-flex', alignItems: 'center', gap: '5px',
                  padding: '4px 10px', borderRadius: '20px',
                  background: 'var(--surf)', fontSize: '0.82rem', color: 'var(--bark)',
                }}>
                  {a.emoji} {a.label}
                  <span style={{ fontWeight: 600, color: 'var(--accent)' }}>×{actCounts[a.id]}</span>
                </span>
              ))}
            </div>
            <p style={{ fontSize: '0.75rem', color: 'var(--stone)', fontStyle: 'italic', marginTop: '0.4rem' }}>
              {weekEntries.length} session{weekEntries.length !== 1 ? 's' : ''} this week
            </p>
          </div>
        )}

        {/* Set target */}
        {!settingTarget ? (
          <button
            className="ghost-btn"
            style={{ fontSize: '0.75rem' }}
            onClick={() => { setSettingTarget(true); setTargetInput(String(monthlyTarget || '')) }}
          >
            {monthlyTarget > 0 ? 'Change target' : 'Set monthly target'}
          </button>
        ) : (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              className="field-input"
              style={{ width: '90px', flex: 'none' }}
              type="number"
              min="0"
              value={targetInput}
              onChange={e => setTargetInput(e.target.value)}
              placeholder="0"
              autoFocus
              onKeyDown={e => {
                if (e.key === 'Enter') saveTarget()
                if (e.key === 'Escape') setSettingTarget(false)
              }}
            />
            <span style={{ fontSize: '0.78rem', color: 'var(--stone)' }}>min / month</span>
            <button className="save-btn ready" style={{ fontSize: '0.75rem', padding: '6px 14px' }} onClick={saveTarget}>Save</button>
            <button className="ghost-btn" style={{ fontSize: '0.72rem' }} onClick={() => setSettingTarget(false)}>Cancel</button>
          </div>
        )}

        {/* Manage activities */}
        <div style={{ marginTop: '1rem', borderTop: '1px solid rgba(0,0,0,0.07)', paddingTop: '0.75rem' }}>
          <button
            className="ghost-btn"
            style={{ fontSize: '0.75rem' }}
            onClick={() => setManageOpen(o => !o)}
          >
            {manageOpen ? 'Close ▲' : 'Manage activities ▼'}
          </button>
          {manageOpen && (
            <div style={{ marginTop: '0.75rem' }}>
              <p style={{ fontSize: '0.75rem', color: 'var(--stone)', marginBottom: '0.6rem' }}>
                Toggle to show or hide activities in your stats and trends.
              </p>
              <div className="act-manage-wrap">
                {ACTIVITY_TYPES.map(a => {
                  const isHidden = hiddenActivities.includes(a.id)
                  return (
                    <button
                      key={a.id}
                      className={`act-manage-chip ${isHidden ? 'hidden-act' : 'visible'}`}
                      onClick={() => onToggleActivity(a.id)}
                    >
                      {a.emoji} {a.label}
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </Card>
    </>
  )
}

// ── Trends ────────────────────────────────────────────────────────────────────

function TrendsSection({ moods, activityLog, visibleActivities }) {
  const [view,        setView]        = useState(7)
  const [showAllActs, setShowAllActs] = useState(false)

  const slice  = filterByDays(moods, view).slice().reverse()
  const count  = slice.length
  const avg    = count ? slice.reduce((s, e) => s + e.level, 0) / count : null
  const streak = calcStreak(moods)

  let trend = null, trendLabel = ''
  if (slice.length >= 4) {
    const half   = Math.floor(slice.length / 2)
    const first  = slice.slice(0, half).reduce((s, e) => s + e.level, 0) / half
    const second = slice.slice(half).reduce((s, e) => s + e.level, 0) / (slice.length - half)
    const diff   = Math.round((second - first) * 10) / 10
    trend      = diff > 0 ? `+${diff}` : diff === 0 ? '→' : `${diff}`
    trendLabel = diff > 0 ? 'Lifting up' : diff < 0 ? 'Needing rest' : 'Steady'
  }

  const actCounts = {}
  visibleActivities.forEach(a => { actCounts[a.id] = 0 })
  Object.values(activityLog).forEach(raw =>
    migrateEntries(raw).forEach(({ id }) => { if (actCounts[id] !== undefined) actCounts[id]++ })
  )
  const maxActCount = Math.max(...Object.values(actCounts), 1)
  const activitiesWithData = visibleActivities
    .filter(a => actCounts[a.id] > 0)
    .sort((a, b) => actCounts[b.id] - actCounts[a.id])

  const displayActs = showAllActs ? activitiesWithData : activitiesWithData.slice(0, 3)

  return (
    <Card label="Trends" title="Progress over time">
      <div className="wb-chart-header">
        <div className="view-toggle">
          {[7, 14, 30].map(d => (
            <button key={d} className={`view-btn${view === d ? ' active' : ''}`} onClick={() => setView(d)}>
              {d === 7 ? '1 week' : d === 14 ? '2 weeks' : 'Month'}
            </button>
          ))}
        </div>
        <span className="wb-chart-title">Mood chart</span>
      </div>

      <div className="stat-row" style={{ marginBottom: '1.5rem' }}>
        {[
          { label: 'Days logged', value: count || '—', sub: count === 0 ? 'start today' : 'in period' },
          { label: 'Avg mood',    value: avg !== null ? (Math.round(avg * 10) / 10).toFixed(1) : '—', sub: avg !== null ? MOOD_STATES[Math.round(avg)].name : 'no data' },
          { label: 'Best streak', value: streak || '—', sub: 'days in a row' },
          { label: 'Trend',       value: trend || '—',  sub: trendLabel || 'log more days' },
        ].map(s => (
          <div key={s.label} className="stat-box">
            <p className="stat-label">{s.label}</p>
            <p className="stat-value">{s.value}</p>
            <p className="stat-sub">{s.sub}</p>
          </div>
        ))}
      </div>

      <div className="dot-chart">
        <div className="dot-row">
          {slice.length === 0 ? (
            <div style={{ fontSize: '0.82rem', color: 'var(--stone)', fontStyle: 'italic', padding: '2rem 0', width: '100%', textAlign: 'center' }}>
              Log your first check-in to see your chart here
            </div>
          ) : (
            slice.map((e, i) => {
              const s = MOOD_STATES[e.level]
              const h = Math.round(((e.level + 1) / 5) * 96)
              return (
                <div key={i} className="dot-col" title={`${e.date} — ${s.name}`}>
                  <div className="dot-bar" style={{ height: h, background: s.color, opacity: s.opacity }} />
                  <span className="dot-date">{e.date}</span>
                </div>
              )
            })
          )}
        </div>
        <div className="chart-legend">
          {MOOD_STATES.map(s => (
            <span key={s.name} className="legend-item">
              <span className="legend-dot" style={{ background: s.color }} />
              {s.name}
            </span>
          ))}
        </div>
      </div>

      {activitiesWithData.length > 0 && (
        <div style={{ marginTop: '1.5rem' }}>
          <p style={{ fontSize: '0.7rem', fontWeight: 500, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--stone)', marginBottom: '0.75rem' }}>
            Sessions by activity (all time)
          </p>
          <div className="bar-chart-wrap">
            {displayActs.map(a => (
              <div key={a.id} className="bar-row">
                <span className="bar-label" style={{ minWidth: 90, fontSize: '0.75rem' }}>
                  {a.emoji} {a.label}
                </span>
                <div className="bar-track">
                  <div className="bar-fill" style={{ width: `${Math.round((actCounts[a.id] / maxActCount) * 100)}%`, background: 'var(--accent)' }}>
                    <span className="bar-val">{actCounts[a.id]}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {activitiesWithData.length > 3 && (
            <button className="wb-show-more" onClick={() => setShowAllActs(s => !s)}>
              {showAllActs ? 'Show less' : `Show ${activitiesWithData.length - 3} more`}
            </button>
          )}
        </div>
      )}
    </Card>
  )
}

// ── Reflections ───────────────────────────────────────────────────────────────

function ReflectionsSection() {
  const [reflections,    setReflections]    = useState([])
  const [refInput,       setRefInput]       = useState('')
  const [refEmoji,       setRefEmoji]       = useState('')
  const [refSaved,       setRefSaved]       = useState(false)
  const [showAll,        setShowAll]        = useState(false)
  const [emojiOpen,      setEmojiOpen]      = useState(null)  // 'new' | r.id | null
  const [editingId,      setEditingId]      = useState(null)
  const [editText,       setEditText]       = useState('')
  const [editEmoji,      setEditEmoji]      = useState('')

  const emojiPickerRef = useRef(null)
  const q = pickReflectionQ()

  useEffect(() => { api.getReflections().then(rows => setReflections(rows || [])) }, [])

  useEffect(() => {
    if (!emojiOpen) return
    function handleClick(e) {
      if (emojiPickerRef.current && !emojiPickerRef.current.contains(e.target)) {
        if (!e.target.closest('.reflection-emoji-btn')) setEmojiOpen(null)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [emojiOpen])

  function save() {
    const text = refInput.trim()
    if (!text) return
    const entry   = { date: todayShort(), q, text, emoji: refEmoji || null }
    const updated = [entry, ...reflections].slice(0, 12)
    setReflections(updated)
    api.saveReflection(entry)
    setRefInput('')
    setRefEmoji('')
    setRefSaved(true)
    setTimeout(() => setRefSaved(false), 2000)
  }

  function startEdit(r) {
    setEditingId(r.id)
    setEditText(r.text)
    setEditEmoji(r.emoji || '')
    setEmojiOpen(null)
  }

  function saveEdit() {
    const text = editText.trim()
    if (!text || !editingId) return
    api.updateReflection(editingId, { text, emoji: editEmoji || null })
    setReflections(prev => prev.map(r => r.id === editingId ? { ...r, text, emoji: editEmoji || null } : r))
    setEditingId(null)
  }

  function cancelEdit() {
    setEditingId(null)
    setEditText('')
    setEditEmoji('')
  }

  const displayRefs = showAll ? reflections : reflections.slice(0, 3)

  return (
    <Card label="Reflections" title="A quiet moment">
      <div className="reflection-box" style={{ marginTop: 0 }}>
        <p className="reflection-q">"{q}"</p>

        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <button
              className="reflection-emoji-btn"
              onClick={() => setEmojiOpen(o => o === 'new' ? null : 'new')}
              title="Add emoji"
            >
              {refEmoji || '🌿'}
            </button>
            {emojiOpen === 'new' && (
              <div className="reflection-emoji-picker" ref={emojiPickerRef}>
                {REFLECTION_EMOJIS.map(e => (
                  <button key={e} className="emoji-pick" onClick={() => { setRefEmoji(e); setEmojiOpen(null) }}>{e}</button>
                ))}
              </div>
            )}
          </div>
          <input
            className="reflection-input"
            type="text"
            placeholder="Write something, anything…"
            value={refInput}
            onChange={e => setRefInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') save() }}
            style={{ flex: 1 }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.75rem', flexWrap: 'wrap' }}>
          <button className="save-btn ready" onClick={save} style={{ fontSize: '0.8rem', padding: '7px 16px' }}>
            Save reflection
          </button>
          {refSaved && <span style={{ fontSize: '0.82rem', color: 'var(--sage)', fontStyle: 'italic' }}>Saved ✓</span>}
        </div>
      </div>

      {reflections.length > 0 && (
        <div style={{ marginTop: '1.25rem' }}>
          <p style={{ fontSize: '0.68rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--stone)', marginBottom: '0.25rem' }}>
            Previous reflections
          </p>
          {displayRefs.map((r, i) => (
            <div key={r.id || i} className="reflection-entry">
              {editingId === r.id ? (
                <div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      <button
                        className="reflection-emoji-btn"
                        onClick={() => setEmojiOpen(o => o === r.id ? null : r.id)}
                        title="Change emoji"
                      >
                        {editEmoji || '🌿'}
                      </button>
                      {emojiOpen === r.id && (
                        <div className="reflection-emoji-picker" ref={emojiPickerRef}>
                          {REFLECTION_EMOJIS.map(e => (
                            <button key={e} className="emoji-pick" onClick={() => { setEditEmoji(e); setEmojiOpen(null) }}>{e}</button>
                          ))}
                        </div>
                      )}
                    </div>
                    <textarea
                      className="field-textarea"
                      rows={2}
                      value={editText}
                      onChange={e => setEditText(e.target.value)}
                      autoFocus
                      style={{ flex: 1, fontSize: '0.88rem' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginTop: '0.5rem' }}>
                    <button className="save-btn ready" style={{ fontSize: '0.75rem', padding: '6px 14px' }} onClick={saveEdit}>Save</button>
                    <button className="ghost-btn" style={{ fontSize: '0.72rem' }} onClick={cancelEdit}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <p style={{ fontSize: '0.72rem', color: 'var(--stone)', marginBottom: 2 }}>
                      {r.emoji && <span style={{ marginRight: '4px' }}>{r.emoji}</span>}
                      {r.date}
                    </p>
                    {r.id && (
                      <button className="expand-toggle" onClick={() => startEdit(r)}>Edit</button>
                    )}
                  </div>
                  <p style={{ fontSize: '0.83rem', color: 'var(--bark)', fontStyle: 'italic', marginBottom: 2 }}>"{r.q}"</p>
                  <p style={{ fontSize: '0.88rem', color: 'var(--ink)' }}>{r.text}</p>
                </div>
              )}
            </div>
          ))}
          {reflections.length > 3 && (
            <button className="wb-show-more" onClick={() => setShowAll(s => !s)}>
              {showAll ? 'Show less' : `Show ${reflections.length - 3} more`}
            </button>
          )}
        </div>
      )}
    </Card>
  )
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function Wellbeing({ initialSection = 'mood' }) {
  const [moods,         setMoods]         = useState([])
  const [activityLog,   setActivityLog]   = useState({})
  const [activeSection, setActiveSection] = useState(initialSection)
  const [actCustom,     setActCustom]     = useState({ hidden: [] })

  useEffect(() => {
    api.getMoods().then(rows => setMoods(rows || []))
    api.getActivities().then(d => setActivityLog(d || {}))
    api.getSetting('act_custom_v1', '{}').then(v => {
      try { setActCustom(JSON.parse(v) || { hidden: [] }) } catch { /* keep default */ }
    })
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => {
      const el = document.getElementById(initialSection)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 150)
    return () => clearTimeout(timer)
  }, [initialSection])

  useEffect(() => {
    function update() {
      const OFFSET = 128
      let current = SECTIONS[0].id
      for (const { id } of SECTIONS) {
        const el = document.getElementById(id)
        if (!el) continue
        if (el.getBoundingClientRect().top <= OFFSET) current = id
      }
      setActiveSection(current)
    }
    update()
    window.addEventListener('scroll', update, { passive: true })
    return () => window.removeEventListener('scroll', update)
  }, [])

  const hiddenActivities  = actCustom.hidden || []
  const visibleActivities = ACTIVITY_TYPES.filter(a => !hiddenActivities.includes(a.id))

  function toggleActivity(id) {
    const isHidden = hiddenActivities.includes(id)
    const newHidden = isHidden
      ? hiddenActivities.filter(h => h !== id)
      : [...hiddenActivities, id]
    const next = { ...actCustom, hidden: newHidden }
    setActCustom(next)
    api.saveSetting('act_custom_v1', JSON.stringify(next))
  }

  return (
    <div className="page active">
      <div className="page-inner">

        <div className="hero">
          <p className="hero-eyebrow">Wellbeing</p>
          <h1>Your <em>Wellbeing</em></h1>
          <hr className="hero-rule" />
          <SectionWelcome text="A gentle record of how you have been feeling — your mood, your energy, and your quiet patterns over time." />
        </div>

        <WellbeingSectionNav activeSection={activeSection} />

        <section id="mood" className="dashboard-section">
          <MoodSection moods={moods} />
        </section>

        <section id="activity" className="dashboard-section">
          <ActivitySection
            activityLog={activityLog}
            hiddenActivities={hiddenActivities}
            onToggleActivity={toggleActivity}
          />
        </section>

        <section id="trends" className="dashboard-section">
          <TrendsSection
            moods={moods}
            activityLog={activityLog}
            visibleActivities={visibleActivities}
          />
        </section>

        <section id="reflections" className="dashboard-section">
          <ReflectionsSection />
        </section>

      </div>
    </div>
  )
}
